// 認証状態 + 家族グループの保持コンテキスト
import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { supabase, isSupabaseEnabled } from '../lib/supabase';
import { Family, getMyFamily, createFamily, joinFamily } from '../lib/db';
import type { User } from '@supabase/supabase-js';

interface AuthContextValue {
  user: User | null;
  family: Family | null;
  loading: boolean;
  /** Supabase未設定の場合true（DB機能を全部無効化） */
  disabled: boolean;
  signInWithMagicLink: (email: string) => Promise<{ error?: string }>;
  signInWithGoogle: () => Promise<{ error?: string }>;
  signOut: () => Promise<void>;
  ensureFamily: () => Promise<Family | null>;
  joinByToken: (token: string) => Promise<Family | null>;
  refreshFamily: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [family, setFamily] = useState<Family | null>(null);
  const [loading, setLoading] = useState(true);
  const disabled = !isSupabaseEnabled();

  const loadFamily = useCallback(async () => {
    const f = await getMyFamily();
    setFamily(f);
  }, []);

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }
    let active = true;
    // 初期化が何らかの理由でハングしても、5秒で必ずロード状態を抜ける保険
    const safety = window.setTimeout(() => {
      if (active) {
        console.warn('[Auth] init timeout — forcing loading=false');
        setLoading(false);
      }
    }, 5000);
    (async () => {
      try {
        const { data, error } = await supabase!.auth.getSession();
        if (error) console.error('[Auth] getSession error', error);
        if (!active) return;
        setUser(data.session?.user || null);
        if (data.session?.user) {
          await loadFamily();
        }
      } catch (e) {
        console.error('[Auth] init exception', e);
      } finally {
        if (active) {
          window.clearTimeout(safety);
          setLoading(false);
        }
      }
    })();
    const { data: sub } = supabase!.auth.onAuthStateChange(async (event, session) => {
      if (!active) return;
      console.log('[Auth] state change:', event);
      setUser(session?.user || null);
      try {
        if (session?.user) {
          await loadFamily();
        } else {
          setFamily(null);
        }
      } catch (e) {
        console.error('[Auth] onAuthStateChange error', e);
      } finally {
        if (active) setLoading(false);
      }
    });
    return () => {
      active = false;
      window.clearTimeout(safety);
      sub.subscription.unsubscribe();
    };
  }, [loadFamily]);

  const signInWithMagicLink = useCallback(async (email: string) => {
    if (!supabase) return { error: 'Supabase未設定' };
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: window.location.origin },
    });
    if (error) return { error: error.message };
    return {};
  }, []);

  const signInWithGoogle = useCallback(async () => {
    if (!supabase) return { error: 'Supabase未設定' };
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin },
    });
    if (error) return { error: error.message };
    return {};
  }, []);

  const signOut = useCallback(async () => {
    if (!supabase) return;
    await supabase.auth.signOut();
    setUser(null);
    setFamily(null);
  }, []);

  /** 家族がまだなければ作成 */
  const ensureFamily = useCallback(async () => {
    const existing = await getMyFamily();
    if (existing) {
      setFamily(existing);
      return existing;
    }
    const created = await createFamily('わが家');
    if (created) setFamily(created);
    return created;
  }, []);

  const joinByToken = useCallback(async (token: string) => {
    const joined = await joinFamily(token);
    if (joined) setFamily(joined);
    return joined;
  }, []);

  const refreshFamily = useCallback(async () => {
    await loadFamily();
  }, [loadFamily]);

  return (
    <AuthContext.Provider
      value={{
        user,
        family,
        loading,
        disabled,
        signInWithMagicLink,
        signInWithGoogle,
        signOut,
        ensureFamily,
        joinByToken,
        refreshFamily,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth は AuthProvider の中で使ってください');
  return ctx;
}
